import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type IPublicClientApplication,
} from "@azure/msal-browser";

import { PortalError, type AccessToken } from "./api/client";

export interface Settings {
  tenantId: string;
  clientId: string;
  scope: string;
  apiBase: string;
}

/**
 * The portal app registration, never the privileged Graph application. A build
 * without these values renders a configuration notice rather than signing in
 * against a guessed tenant.
 */
export function settings(): Settings | null {
  const tenantId = import.meta.env.VITE_TENANT_ID;
  const clientId = import.meta.env.VITE_PORTAL_CLIENT_ID;
  if (!tenantId || !clientId) return null;
  return {
    tenantId,
    clientId,
    scope: import.meta.env.VITE_API_SCOPE ?? `api://${clientId}/access_as_user`,
    apiBase: import.meta.env.VITE_API_BASE ?? "",
  };
}

export function application(config: Settings): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      // Entra matches this string exactly, and a registered URI must carry a
      // path, so the origin alone would never match what infra/portal declares.
      redirectUri: `${window.location.origin}/`,
    },
    // Audit records name people, so a closed tab must not leave a token that
    // still reads them.
    cache: { cacheLocation: "sessionStorage" },
  });
}

export function accessToken(
  instance: IPublicClientApplication,
  scope: string,
): AccessToken {
  return async () => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) throw new PortalError(401, "sign in to read audit evidence");
    try {
      const result = await instance.acquireTokenSilent({ scopes: [scope], account });
      return result.accessToken;
    } catch (cause) {
      if (cause instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ scopes: [scope], account });
      }
      throw new PortalError(401, "sign in again to read audit evidence");
    }
  };
}
